import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { WEEKDAYS } from "@/lib/format";
import { Plus, Trash2, Clock, CalendarDays, Repeat, Info, ChevronLeft, ChevronRight } from "lucide-react";

const IT_DAYS = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtShort = (d) => d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

function DayRow({ date, slots, onAdd, onDel }) {
  const [t, setT] = useState({ start: "09:00", end: "13:00" });
  const d = new Date(date + "T00:00:00");
  const isPast = date < iso(new Date());
  return (
    <Card data-testid={`day-row-${date}`} className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-[#102A2A]">{IT_DAYS[d.getDay()]}</p>
          <p className="text-xs text-[#5C6B68]">{fmtShort(d)}</p>
        </div>
        {slots.length === 0 && <span className="text-sm text-[#5C6B68]">Nessun turno</span>}
      </div>
      {slots.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {slots.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-2 bg-[#E0EFEA] text-[#087A73] rounded-full px-3 py-1.5 text-sm font-semibold">
              <Clock className="h-3.5 w-3.5" /> {s.start_time}-{s.end_time}
              <button data-testid={`del-slot-${s.id}`} onClick={() => onDel(s.id)} className="text-[#C0392B]"><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      {!isPast && (
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Dalle</Label><Input data-testid={`day-start-${date}`} type="time" value={t.start} onChange={(e) => setT({ ...t, start: e.target.value })} className="h-10 rounded-xl mt-1 w-32" /></div>
          <div><Label className="text-xs">Alle</Label><Input data-testid={`day-end-${date}`} type="time" value={t.end} onChange={(e) => setT({ ...t, end: e.target.value })} className="h-10 rounded-xl mt-1 w-32" /></div>
          <Button data-testid={`day-add-${date}`} onClick={() => onAdd(date, t.start, t.end)} variant="outline" className="h-10 rounded-xl border-2 border-[#087A73] text-[#087A73] hover:bg-[#087A73]/10"><Plus className="mr-1 h-4 w-4" /> Turno</Button>
        </div>
      )}
    </Card>
  );
}

export default function Disponibilita() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["prof-availability"], queryFn: async () => (await api.get("/professional/availability")).data });
  const [weekly, setWeekly] = useState({ weekday: "0", start_time: "09:00", end_time: "13:00" });
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const slots = data?.slots || [];

  const recurring = slots.filter((s) => !s.date);
  const specific = slots.filter((s) => s.date);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["prof-availability"] });

  const addWeekly = async () => {
    try { await api.post("/professional/availability", { weekday: Number(weekly.weekday), start_time: weekly.start_time, end_time: weekly.end_time }); toast.success("Orario abituale aggiunto."); invalidate(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addDated = async (date, start, end) => {
    try { await api.post("/professional/availability", { date, start_time: start, end_time: end }); toast.success("Turno aggiunto."); invalidate(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/professional/availability/${id}`); invalidate(); };

  const byDay = WEEKDAYS.map((d, i) => ({ day: d, idx: i, slots: recurring.filter((s) => s.weekday === i) }));
  const weekDays = Array.from({ length: 7 }, (_, i) => iso(addDays(weekStart, i)));
  const weekLabel = `${fmtShort(weekStart)} – ${fmtShort(addDays(weekStart, 6))}`;
  const isThisWeek = iso(weekStart) === iso(startOfWeek(new Date()));

  return (
    <DashboardLayout title="Disponibilità">
      <div className="max-w-3xl space-y-8">
        <div className="flex items-start gap-2 bg-[#EAF3EF] border border-[#D2E3DB] text-[#5C6B68] rounded-xl px-4 py-3 text-sm">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#087A73]" /> Hai turni diversi ogni settimana? Usa il <strong>pianificatore settimanale</strong> per impostare gli orari di ogni giorno, settimana per settimana. Gli "orari abituali" sono un modello ricorrente opzionale.
        </div>

        {/* WEEK PLANNER */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="font-bold text-[#102A2A] flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#087A73]" /> Pianifica la settimana</h3>
            <div className="flex items-center gap-2">
              <Button data-testid="week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))} variant="outline" className="h-10 w-10 p-0 rounded-xl border-2 border-[#D2E3DB]"><ChevronLeft className="h-5 w-5" /></Button>
              <span data-testid="week-label" className="font-semibold text-[#102A2A] min-w-[140px] text-center text-sm">{weekLabel}</span>
              <Button data-testid="week-next" onClick={() => setWeekStart(addDays(weekStart, 7))} variant="outline" className="h-10 w-10 p-0 rounded-xl border-2 border-[#D2E3DB]"><ChevronRight className="h-5 w-5" /></Button>
              {!isThisWeek && <Button data-testid="week-today" onClick={() => setWeekStart(startOfWeek(new Date()))} variant="outline" className="h-10 rounded-xl border-2 border-[#087A73] text-[#087A73] text-sm px-3">Oggi</Button>}
            </div>
          </div>
          {isLoading ? <LoadingBlock /> : (
            <div className="space-y-3">
              {weekDays.map((date) => (
                <DayRow key={date} date={date} slots={specific.filter((s) => s.date === date)} onAdd={addDated} onDel={del} />
              ))}
            </div>
          )}
        </div>

        {/* RECURRING WEEKLY (optional base) */}
        <div className="space-y-4">
          <h3 className="font-bold text-[#102A2A] flex items-center gap-2"><Repeat className="h-5 w-5 text-[#087A73]" /> Orari abituali (ricorrenti ogni settimana)</h3>
          <Card className="space-y-4">
            <div className="grid sm:grid-cols-4 gap-3 items-end">
              <div>
                <Label>Giorno</Label>
                <Select value={weekly.weekday} onValueChange={(v) => setWeekly({ ...weekly, weekday: v })}>
                  <SelectTrigger data-testid="slot-weekday" className="h-12 rounded-xl mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Dalle</Label><Input data-testid="slot-start" type="time" value={weekly.start_time} onChange={(e) => setWeekly({ ...weekly, start_time: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
              <div><Label>Alle</Label><Input data-testid="slot-end" type="time" value={weekly.end_time} onChange={(e) => setWeekly({ ...weekly, end_time: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
              <Button data-testid="slot-add" onClick={addWeekly} className="bg-[#087A73] text-white rounded-xl h-12"><Plus className="mr-1 h-4 w-4" /> Aggiungi</Button>
            </div>
          </Card>
          {!isLoading && (
            <div className="space-y-3">
              {byDay.map((d) => (
                <Card key={d.idx} className="flex items-center justify-between">
                  <span className="font-semibold text-[#102A2A] w-28">{d.day}</span>
                  <div className="flex-1 flex flex-wrap gap-2">
                    {d.slots.length === 0 ? <span className="text-sm text-[#5C6B68]">Non disponibile</span> :
                      d.slots.map((s) => (
                        <span key={s.id} className="inline-flex items-center gap-2 bg-[#E0EFEA] text-[#087A73] rounded-full px-3 py-1.5 text-sm font-semibold">
                          <Clock className="h-3.5 w-3.5" /> {s.start_time}-{s.end_time}
                          <button data-testid={`del-slot-${s.id}`} onClick={() => del(s.id)} className="text-[#C0392B]"><Trash2 className="h-3.5 w-3.5" /></button>
                        </span>
                      ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
